import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { BarChart2, Eye, History, ArrowUpDown, RefreshCw } from 'lucide-react';
import { getBacktestsList, SavedBacktest } from '@/services/backtestStorageService';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { BacktestDetailsView } from './BacktestDetailsView';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export const BacktestHistoryWidget = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [backtests, setBacktests] = useState<SavedBacktest[]>([]);
  const [filteredBacktests, setFilteredBacktests] = useState<SavedBacktest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'date' | 'performance'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedBacktest, setSelectedBacktest] = useState<SavedBacktest | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  // Fetch the list of backtests on component mount
  useEffect(() => {
    loadBacktests();
  }, []);

  // Get the current user's ID
  const getUserId = async () => {
    const { data } = await supabase.auth.getUser();
    return data.user?.id;
  };

  // Load backtests from the database
  const loadBacktests = async () => {
    setLoading(true);
    
    try {
      const userId = await getUserId();
      if (!userId) {
        setLoading(false);
        return;
      }

      const backtestsList = await getBacktestsList(userId);
      setBacktests(backtestsList);
      filterAndSortBacktests(backtestsList);
    } catch (error) {
      console.error("Error loading backtests:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort backtests
  useEffect(() => {
    filterAndSortBacktests(backtests);
  }, [searchTerm, sortField, sortDirection]);

  const filterAndSortBacktests = (backtests: SavedBacktest[]) => {
    let filtered = [...backtests];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        backtest => 
          backtest.name.toLowerCase().includes(term) ||
          backtest.description?.toLowerCase().includes(term) ||
          backtest.strategyName?.toLowerCase().includes(term)
      );
    }

    // Sort the results
    filtered.sort((a, b) => {
      if (sortField === 'date') {
        return sortDirection === 'asc'
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else {
        // Performance sorting (by total return)
        const aReturn = a.performance?.totalReturn || 0;
        const bReturn = b.performance?.totalReturn || 0;
        return sortDirection === 'asc' ? aReturn - bReturn : bReturn - aReturn;
      }
    });

    // Only show top 5 results for the widget
    setFilteredBacktests(filtered.slice(0, 5));
  };

  // Toggle sort direction
  const toggleSort = (field: 'date' | 'performance') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Open details dialog for a backtest
  const viewBacktestDetails = (backtest: SavedBacktest) => {
    setSelectedBacktest(backtest);
    setShowDetailsDialog(true);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  };

  // Render status badge
  const renderStatusBadge = (status: string) => {
    let className = '';
    
    switch (status) {
      case 'completed':
        className = 'bg-green-100 text-green-800 border-green-200';
        break;
      case 'running':
        className = 'bg-blue-100 text-blue-800 border-blue-200';
        break;
      case 'paused':
        className = 'bg-yellow-100 text-yellow-800 border-yellow-200';
        break;
      case 'error':
        className = 'bg-red-100 text-red-800 border-red-200';
        break;
      default:
        className = 'bg-gray-100 text-gray-800 border-gray-200';
    }
    
    return (
      <Badge variant="outline" className={`${className} capitalize`}>
        {status}
      </Badge>
    );
  };

  // Empty state component
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="max-w-sm mx-auto text-center">
        <div className="p-3 bg-blue-50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <BarChart2 className="h-8 w-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Test Your Trading Ideas</h3>
        <p className="text-sm text-gray-500 mb-6">
          Start by creating and testing your trading strategies with historical data. 
          View your backtest results here to analyze performance before deploying.
        </p>
        <div className="space-y-3">
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => {
              // Trigger the parent component to show strategy creation
              window.dispatchEvent(new CustomEvent('showStrategyCreation'));
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            Create & Test Strategy
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/backtest-history')}
            className="w-full"
          >
            View Full Testing History
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-medium">Recent Backtests</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 px-2"
              onClick={loadBacktests}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/backtest-history')}
              className="text-xs"
            >
              <History className="h-3 w-3 mr-1" />
              View All
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <Input
            placeholder="Search backtests..."
            className="text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        ) : filteredBacktests.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>
                    <button 
                      className="flex items-center" 
                      onClick={() => toggleSort('date')}
                    >
                      Date
                      <ArrowUpDown className={`ml-1 h-3 w-3 ${sortField === 'date' ? 'opacity-100' : 'opacity-30'}`} />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button 
                      className="flex items-center" 
                      onClick={() => toggleSort('performance')}
                    >
                      Return
                      <ArrowUpDown className={`ml-1 h-3 w-3 ${sortField === 'performance' ? 'opacity-100' : 'opacity-30'}`} />
                    </button>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBacktests.map((backtest) => (
                  <TableRow key={backtest.id}>
                    <TableCell className="font-medium text-sm">{backtest.name}</TableCell>
                    <TableCell className="text-xs">{formatDate(backtest.createdAt)}</TableCell>
                    <TableCell className={`font-medium text-sm ${
                      (backtest.performance?.totalReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(backtest.performance?.totalReturn || 0) >= 0 ? '+' : ''}
                      {(backtest.performance?.totalReturn || 0).toFixed(2)}%
                    </TableCell>
                    <TableCell>{renderStatusBadge(backtest.status)}</TableCell>
                    <TableCell className="text-right p-0 pr-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => viewBacktestDetails(backtest)}
                        title="View Details"
                        className="h-8 w-8"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Backtest Details Dialog */}
      {selectedBacktest && (
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-screen-lg h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedBacktest.name}</DialogTitle>
            </DialogHeader>
            <BacktestDetailsView backtestId={selectedBacktest.id} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};